import { MecProduct } from '@adapters/bigcommerce/types/product';
import SectionAccordion from '_components/UI/SectionAccordion';
import { useTranslation } from 'next-i18next';
import RichTextContainer from '@components/cms/RichTextContainer';
import SizeAndFit from '../SizeAndFit';
import Table from '@components/ui/Table';
import Tooltip from '_components/UI/Tooltip';
import DOMPurify from 'isomorphic-dompurify';
import { SizeAndFitProps } from '../SizeAndFit';
import {
  FitSizeValueInterface,
  SizeChartInterface,
} from '@adapters/bigcommerce/types/sizeChart';

import styles from './ProductInformationSection.module.scss';

const renderTechSpecValue = (
  value: string | string[],
  groupClassName?: string
) => {
  if (Array.isArray(value)) {
    return (
      <ul className={groupClassName}>
        {value.map((item) => (
          <li key={item}>
            <span
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(item),
              }}
            />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <span
      dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(value),
      }}
    />
  );
};

const ProductInformationSection = ({
  product,
  fitSentimentData,
  measureData,
  fitAndRise,
  scrollToProductReviewsAndAnswers,
  isGiftCard,
}: {
  product: MecProduct;
  fitSentimentData: SizeAndFitProps['fitSentimentData'];
  measureData: SizeChartInterface;
  fitAndRise: FitSizeValueInterface[] | null;
  isBike: boolean;
  scrollToProductReviewsAndAnswers?: () => void;
  isGiftCard: boolean;
}) => {
  const { t } = useTranslation('pdp');

  return (
    <div className={styles.productInformationSection}>
      {product?.description && (
        <SectionAccordion
          icon="caret"
          className={styles.productDetailsAccordion}
        >
          <SectionAccordion.Header title={t('product.details')} />
          <SectionAccordion.Collapsible>
            <RichTextContainer classNames={styles.productDetailsRichText}>
              <div className={styles.productCode}>
                {t('product.sku.code')} {product?.code}
              </div>
              <div
                className={styles.productDetails}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(product?.description),
                }}
              />
            </RichTextContainer>
          </SectionAccordion.Collapsible>
        </SectionAccordion>
      )}
      {fitSentimentData?.fitSentiment?.total !== 0 && (
        <SectionAccordion icon="caret" className={styles.sizeAndFitAccordion}>
          <SectionAccordion.Header title={t('product.sizeAndFit.title')} />
          <SectionAccordion.Collapsible>
            <div className={styles.sizeAndFitContainer}>
              <SizeAndFit
                fitSentimentData={fitSentimentData}
                sizeDetail={product?.sizeData}
                measure={measureData}
                fitAndRise={fitAndRise}
                isBike={product?.isBike}
                scrollToProductReviewsAndAnswers={
                  scrollToProductReviewsAndAnswers
                }
              />
            </div>
          </SectionAccordion.Collapsible>
        </SectionAccordion>
      )}
      {product?.techSpecs && !isGiftCard && (
        <SectionAccordion icon="caret">
          <SectionAccordion.Header
            title={t('product.techSpecs')}
            className={styles.productInfoAccordionHeading}
          />
          <SectionAccordion.Collapsible>
            <div className={styles.productInfoAccordionTableWrapper}>
              <div className={styles.productInfoAccordionTable}>
                <Table
                  dataRows={product?.techSpecs.map((techSpec, i) => [
                    techSpec.description ? (
                      <span className={styles.techSpecKey}>
                        {techSpec.key.split(' ').slice(0, -1).join(' ')}{' '}
                        <span className={styles.techSpecKeyNoBreak}>
                          {techSpec.key.split(' ').at(-1)}
                          <Tooltip
                            className={styles.tooltip}
                            content={techSpec.description}
                            id={`tech-spec-tooltip-${techSpec.key}-${i}`}
                            iconSize={15}
                          />
                        </span>
                      </span>
                    ) : (
                      <span className={styles.techSpecKey}>{techSpec.key}</span>
                    ),
                    renderTechSpecValue(
                      techSpec.value,
                      styles.techSpecValueGroup
                    ),
                  ])}
                />
              </div>

              {product.quebecRepairDisclosureFlag && (
                <div
                  className={styles.qcText}
                  dangerouslySetInnerHTML={{
                    __html: t('common:qc.bill29.text'),
                  }}
                />
              )}
            </div>
          </SectionAccordion.Collapsible>
        </SectionAccordion>
      )}
    </div>
  );
};

export default ProductInformationSection;
